/**
 * Cookie Consent Manager
 * Handles user consent for analytics tracking (GA4, Clarity, PostHog)
 * Implements Google Consent Mode v2 with region-scoped policy.
 *
 * Policy model (2026-07-12 region-scoped revision):
 * - Region policy: 'prior-consent' (EEA/UK/CH or unknown country) requires an
 *   explicit accept before any analytics runs; 'notice-opt-out' (rest of
 *   world) runs analytics by default with a clear notice and opt-out.
 * - User decision: 'undecided' | 'granted' | 'denied'. A stored decision
 *   ALWAYS overrides the regional default — a denial blocks analytics in
 *   every region, a grant enables it in every region.
 * - Effective permission: resolved centrally by isAnalyticsAllowed(); GA4,
 *   Clarity, and PostHog all consume the same result (the inline loaders in
 *   layout.tsx mirror the same logic synchronously, before React).
 *
 * The region policy is derived in src/proxy.ts from x-vercel-ip-country and
 * delivered as the pm_region_policy cookie. Only the derived POLICY is
 * stored in the browser — never the country itself. A missing/unknown
 * cookie falls back to 'prior-consent' (conservative).
 *
 * Storage is fail-safe: if localStorage is unavailable or throws, decisions
 * still apply for the session via an in-memory mirror, banner actions never
 * throw, and a dev-only console.warn leaves a diagnosable signal.
 */

export type ConsentStatus = 'granted' | 'denied'
export type RegionPolicy = 'prior-consent' | 'notice-opt-out'
export type UserDecision = 'undecided' | 'granted' | 'denied'
export type DecisionSource =
  | 'banner-accept'
  | 'banner-decline'
  | 'notice-ack'
  | 'notice-opt-out'
  | 'settings'
  | 'legacy-migration'

export interface ConsentState {
  analytics: ConsentStatus
  ad_storage: ConsentStatus
  ad_user_data: ConsentStatus
  ad_personalization: ConsentStatus
  timestamp: number
  /** How the decision was made (absent on pre-2026-07-12 records). */
  source?: DecisionSource
}

// v2 keys: the pre-2026-07 implementation auto-granted consent on
// scroll/timer and persisted fabricated "granted" records under the v1 keys.
// Explicit accepts and fabricated grants are indistinguishable there, so v2
// trusts only records written by an explicit banner decision. Legacy DENIED
// records are migrated (never override a refusal); legacy GRANTED records
// are dropped so the banner asks again.
//
// The v2 record shape is unchanged by the region-scoped revision (the new
// `source` field is optional), so existing v2 grants and denials are
// preserved as-is — no wipe, no re-prompt.
const CONSENT_STORAGE_KEY = 'pixelmojo_cookie_consent_v2'
export const CONSENT_MIRROR_KEY = 'pm_consent_v2'
export const REGION_POLICY_COOKIE = 'pm_region_policy'
const LEGACY_CONSENT_STORAGE_KEY = 'pixelmojo_cookie_consent'
const LEGACY_MIRROR_KEY = 'pm_consent'

// ---------------------------------------------------------------------------
// Fail-safe storage. localStorage can be unavailable (Safari Private mode,
// storage disabled, embedded contexts) — a consent decision must still take
// effect for the session and the banner action must still complete.
// ---------------------------------------------------------------------------

let memoryState: ConsentState | null = null

function consentStorageWarn(err: unknown): void {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      '[consent] localStorage unavailable — consent held in memory for this session only',
      err
    )
  }
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch (err) {
    consentStorageWarn(err)
    return null
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch (err) {
    consentStorageWarn(err)
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch (err) {
    consentStorageWarn(err)
  }
}

// ---------------------------------------------------------------------------
// Region policy
// ---------------------------------------------------------------------------

/**
 * Read the region policy derived by src/proxy.ts. Conservative fallback:
 * anything other than an explicit 'notice-opt-out' cookie (missing cookie,
 * unknown value, SSR, cookie access error) means 'prior-consent'.
 */
export function getRegionPolicy(): RegionPolicy {
  if (typeof document === 'undefined') return 'prior-consent'
  try {
    const match = document.cookie.match(/(?:^|;\s*)pm_region_policy=([^;]*)/)
    if (match && match[1] === 'notice-opt-out') return 'notice-opt-out'
  } catch {
    // Cookie access blocked — stay conservative.
  }
  return 'prior-consent'
}

// ---------------------------------------------------------------------------
// User decision + effective permission
// ---------------------------------------------------------------------------

/**
 * One-time lazy migration from the v1 keys. Runs only when no v2 record
 * exists. Preserves an explicit denial; discards untrusted grants.
 */
function migrateLegacyConsent(): ConsentState | null {
  try {
    const legacy = safeGetItem(LEGACY_CONSENT_STORAGE_KEY)
    const legacyMirror = safeGetItem(LEGACY_MIRROR_KEY)
    if (legacy === null && legacyMirror === null) return null

    let denied = legacyMirror === 'denied'
    if (!denied && legacy) {
      const s = JSON.parse(legacy) as Partial<ConsentState>
      denied = s?.analytics === 'denied'
    }

    // Remove legacy keys either way — fabricated grants must not survive
    safeRemoveItem(LEGACY_CONSENT_STORAGE_KEY)
    safeRemoveItem(LEGACY_MIRROR_KEY)

    if (!denied) return null

    const state: ConsentState = {
      analytics: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      timestamp: Date.now(),
      source: 'legacy-migration',
    }
    safeSetItem(CONSENT_STORAGE_KEY, JSON.stringify(state))
    safeSetItem(CONSENT_MIRROR_KEY, 'denied')
    return state
  } catch {
    return null
  }
}

/**
 * Get current consent state from localStorage (or the in-memory mirror when
 * persistence is unavailable).
 */
export function getConsentState(): ConsentState | null {
  if (typeof window === 'undefined') return null

  const stored = safeGetItem(CONSENT_STORAGE_KEY)
  if (stored) {
    try {
      const state = JSON.parse(stored) as ConsentState
      // Check if consent is older than 1 year (re-consent required)
      const ONE_YEAR = 365 * 24 * 60 * 60 * 1000
      if (Date.now() - state.timestamp > ONE_YEAR) {
        safeRemoveItem(CONSENT_STORAGE_KEY)
        safeRemoveItem(CONSENT_MIRROR_KEY)
        memoryState = null
        return null
      }
      return state
    } catch {
      // Corrupted record — fall through to memory/legacy paths.
    }
  }

  // Persistence failed earlier this session (or is unavailable): the
  // in-memory decision still governs this page's analytics.
  if (memoryState) return memoryState

  return migrateLegacyConsent()
}

/** The user's explicit decision, if any. */
export function getUserDecision(): UserDecision {
  const state = getConsentState()
  if (!state) return 'undecided'
  return state.analytics === 'granted' ? 'granted' : 'denied'
}

/**
 * Effective analytics permission — THE central resolver every tracker
 * consumes (GA4 event sites, Clarity, PostHog; the inline loaders in
 * layout.tsx mirror it synchronously):
 * - explicit grant  -> allowed (any region)
 * - explicit denial -> blocked (any region, overrides every default)
 * - undecided       -> allowed only under the 'notice-opt-out' region policy
 */
export function isAnalyticsAllowed(): boolean {
  const decision = getUserDecision()
  if (decision === 'granted') return true
  if (decision === 'denied') return false
  return getRegionPolicy() === 'notice-opt-out'
}

/**
 * @deprecated Alias for isAnalyticsAllowed(). Kept so the ~30 existing
 * event call sites keep working; they intentionally receive the centrally
 * resolved effective permission (which includes the notice-opt-out regional
 * default), not only explicit grants.
 */
export function isAnalyticsConsentGranted(): boolean {
  return isAnalyticsAllowed()
}

// ---------------------------------------------------------------------------
// Persistence + tracker signaling
// ---------------------------------------------------------------------------

/**
 * Save consent state. Never throws: always updates the in-memory mirror,
 * then best-effort persists to localStorage.
 */
export function saveConsentState(state: Omit<ConsentState, 'timestamp'>): void {
  if (typeof window === 'undefined') return

  const fullState: ConsentState = {
    ...state,
    timestamp: Date.now(),
  }

  memoryState = fullState
  safeSetItem(CONSENT_STORAGE_KEY, JSON.stringify(fullState))
}

/**
 * Update Google Consent Mode
 */
export function updateGoogleConsent(
  analytics: ConsentStatus,
  ads: ConsentStatus = 'denied'
): void {
  if (typeof window === 'undefined') return

  // Update Google Consent Mode
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: analytics,
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
    })
  }
}

/**
 * Update Microsoft Clarity consent using ConsentV2 API
 *
 * IMPORTANT: Clarity does NOT automatically read Google Consent Mode.
 * We must explicitly call the ConsentV2 API to send consent signals.
 *
 * As of Oct 31, 2025, Clarity enforces consent for EEA/UK/CH users.
 * Using the new ConsentV2 API ensures compliance with regional regulations.
 *
 * @param consent - Whether analytics storage is granted or denied
 * @param shouldStop - Whether to completely stop Clarity tracking (only when user actively declines)
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-consent
 */
export function updateClarityConsent(
  consent: ConsentStatus,
  shouldStop: boolean = false
): void {
  if (typeof window === 'undefined') return

  // A DOM element with id="clarity" is exposed as window.clarity, so a
  // truthy value is not proof the Clarity queue exists. Only call a function.
  if (typeof window.clarity === 'function') {
    if (consent === 'granted') {
      // Enable analytics tracking with ConsentV2 API
      window.clarity('consentv2', {
        ad_storage: 'denied', // We don't use ads
        analytics_storage: 'granted',
      })
    } else {
      // Set to denied mode (cookieless tracking)
      window.clarity('consentv2', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
      })

      // Only stop if explicitly requested (user actively declines)
      if (shouldStop) {
        window.clarity('stop')
      }
    }
  }
}

/**
 * Grant consent for analytics. In a 'notice-opt-out' region the banner's
 * acknowledge action records source 'notice-ack'; an explicit accept in a
 * 'prior-consent' region records 'banner-accept'. Never throws — analytics
 * initializes even when persistence fails (in-memory state + events).
 */
export function grantConsent(source: DecisionSource = 'banner-accept'): void {
  const state: Omit<ConsentState, 'timestamp'> = {
    analytics: 'granted',
    ad_storage: 'denied', // We don't use ads, so keep denied
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    source,
  }

  saveConsentState(state)
  safeSetItem(CONSENT_MIRROR_KEY, 'granted')
  updateGoogleConsent('granted', 'denied')
  updateClarityConsent('granted', false) // Don't stop tracking

  // Dispatch event for components that need to know
  window.dispatchEvent(new CustomEvent('consentGranted'))
}

/**
 * Deny consent for analytics (explicit reject, or opt-out in a
 * notice-opt-out region). Takes effect immediately and persistently: a
 * stored denial overrides every regional default. Never throws.
 */
export function denyConsent(source: DecisionSource = 'banner-decline'): void {
  const state: Omit<ConsentState, 'timestamp'> = {
    analytics: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    source,
  }

  saveConsentState(state)
  safeSetItem(CONSENT_MIRROR_KEY, 'denied')
  updateGoogleConsent('denied', 'denied')
  updateClarityConsent('denied', true) // Stop tracking when user actively declines

  // Dispatch event for components that need to know
  window.dispatchEvent(new CustomEvent('consentDenied'))
}

/**
 * Check if user has made a consent decision
 */
export function hasConsentDecision(): boolean {
  return getConsentState() !== null
}

/**
 * Initialize consent state on page load. Applies the stored decision when
 * one exists; otherwise applies the regional default (granted signals under
 * notice-opt-out, denied under prior-consent). Runs before any gated
 * analytics work — the inline loaders in layout.tsx make the same call
 * synchronously for script injection.
 */
export function initializeConsent(): void {
  if (typeof window === 'undefined') return

  const existingConsent = getConsentState()

  if (existingConsent) {
    // User has previously made a decision - apply it
    updateGoogleConsent(existingConsent.analytics, existingConsent.ad_storage)
    updateClarityConsent(existingConsent.analytics, false) // Don't stop on re-init
  } else if (getRegionPolicy() === 'notice-opt-out') {
    // Rest-of-world default: analytics runs with notice + opt-out. No
    // consent record is written — the user remains 'undecided' and a later
    // opt-out takes priority over this default.
    updateGoogleConsent('granted', 'denied')
    updateClarityConsent('granted', false)
  } else {
    // Prior-consent region (or unknown country) with no decision: denied by
    // default. Nothing loads pre-decision.
    updateGoogleConsent('denied', 'denied')
    updateClarityConsent('denied', false) // Don't stop, just denied mode
  }
}

/**
 * Ask the CookieConsent banner to reopen so the user can change their
 * decision (e.g. the footer "Cookie Settings" link)
 */
export function reopenConsentBanner(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('pm-consent-reopen'))
}

/**
 * Clear consent state (for testing)
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return
  memoryState = null
  safeRemoveItem(CONSENT_STORAGE_KEY)
  safeRemoveItem(CONSENT_MIRROR_KEY)
}
